/*
 * Copyright (c) 2014-2023 Comparatio
 */

import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { FormGroup, FormControl, Validators} from '@angular/forms';
import {Page} from '../../model/page';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/delay';
import {ToastrService} from 'ngx-toastr';
import { ActivatedRoute, ActivatedRouteSnapshot, Router } from '@angular/router';
import {permissions} from '../../permissions';
import { ZeroOutInventoryService } from '../../services/zero-out-inventory.service';
import { ConfirmationService } from 'primeng/api';
import { TableExtensionService } from './services/table-extension.service';
import { SortService } from './services/sort.service';
import { AuthService } from '../../auth/auth.service';
import { Title } from '@angular/platform-browser';
import { Subscription } from 'rxjs/Subscription';
import { IZeroOutInventory } from './interfaces/IZeroOutInventory.js';
import tableCols from './zero-out-inventory.cols';
import { CustomerService } from 'app/services/customer.service';
import { DatesService } from 'app/services/dates.service';
import { DisplayFieldPipe } from '../pipes/displayField.pipe';
import { CompanyService } from "app/services/company.service";

@Component ( {
	             providers: [
					ZeroOutInventoryService,
					AuthService,
					TableExtensionService,
					SortService,
					DatesService,
				],
				selector: 'app-zero-out-inventory',
				templateUrl: './zero-out-inventory.component.html',
	             styleUrls: ['./zero-out-inventory.component.scss'],
})
export class ZeroOutInventoryComponent implements OnInit, OnDestroy {
	@ViewChild ( 'dataView' ) dataView: ElementRef;
	@ViewChild ( 'actionbar' ) actionbar: ElementRef;
	@ViewChild ( 'actionbarfoot' ) actionbarfoot: ElementRef;
	@ViewChild ( 'content' ) content: ElementRef;
	@ViewChild ( 'tableHeader' ) tableHeader: ElementRef;
	@ViewChild ( 'paginator' ) paginator: ElementRef;
	@ViewChild('tableFilter') tableFilter: ElementRef;
	public loading = false;
	public page = new Page();
	public cols = [];
	public rows = [];
	public selected: any = null;
	public totalRecords = 0;
	public filtersApplied: boolean;
	public scrollHeight = '50px';
	private viewportHeight = 0;
	private viewportHeightListener: boolean;
	private subscriptions: Array<Subscription>;
	public size = 50;
	public userActionLoading = false;
	public sortApplied: boolean;
	public sortField: string;
	public sortOrder: number;
	public newline: number = null;
	public editRow = false;
	public rowSelection: number;
	public record: IZeroOutInventory;
	public edit: IZeroOutInventory;
	public errors: Array<any> = [];
	public action: string = '';
	public permissions = permissions;
	public customerList: Array<any> = [];
	public companyList: Array<any> = [];
	public hideTooltip = false;
	public viewFilters = true;
	
	constructor (
		private zeroOutInventoryService: ZeroOutInventoryService,
		private customerService: CustomerService,
		private toastr : ToastrService,
		private sortService: SortService,
		private router : Router,
		private route : ActivatedRoute,
		private confirmationService : ConfirmationService,
		private authService: AuthService,
		private title : Title,
		private tableExtensionService : TableExtensionService,
		private datesService: DatesService,
		private displayFieldPipe: DisplayFieldPipe,
		private companyService: CompanyService,
	) {
		this.page.pageNumber = 0;
		this.page.size = 100;
		this.title.setTitle ( 'Zero Out Inventory' );
	}
	
	public ngOnInit () {
		this.loading = true;
		this.filtersApplied = false;
		this.sortApplied = false;
		this.sortField = '';
		this.sortOrder = 0;
	
		this.viewportHeightListener = true;
	
		const filterParams = this.route.snapshot.queryParams;
		this.parseParams(filterParams);

		this.subscriptions = [];
		this.cols = tableCols;
		
		this.record = this.zeroOutInventoryService.initialize ();
		this.edit = this.zeroOutInventoryService.initialize ();
		
		this.companyService.getShortCompanyList().subscribe(response => {
			this.companyList = response.companyData;
		});
		
		this.getData(false);
		this.screenHeightListener();
		this.viewFilters = true;
	}
	
	public ngOnDestroy () : void {
		this.viewportHeightListener = false;
		for ( const element of this.subscriptions ) {
			if ( element ) {
				element.unsubscribe();
			}
		}
	}
	
	public getData (reloadWithParams = true) {
		this.setPage({ offset: this.page.pageNumber }, reloadWithParams);
	}
	
	public setPage (pageInfo, reloadWithParams = true) {
		if (reloadWithParams) {
			this.page.filterParams['page'] = pageInfo.offset ? pageInfo.offset : 0;
			this.page.filterParams['perPage'] = pageInfo.size
			                                    ? pageInfo.size
			                                    : this.size;
			this.router.routeReuseStrategy.shouldReuseRoute = function (
				future: ActivatedRouteSnapshot,
				curr: ActivatedRouteSnapshot
			) {
				return false;
			};
			this.router.navigate([], {
				relativeTo: this.route,
				queryParams: this.page.filterParams,
			});
		} else {
			this.loading = true;
			this.selected = null;
			this.page.pageNumber = pageInfo.offset;
			this.subscriptions.push (
				this.zeroOutInventoryService.getDataList ( this.page ).subscribe ( result => {
					this.rows = result.data;
					this.totalRecords = result.count;
					
					setTimeout ( () => {
						this.scrollHeight = this.getScrollHeight () + 'px';
					} );
					
					this.loading = false;
				}, () => {
					this.loading = false;
					window.dispatchEvent ( new Event ( 'resize' ) );
					this.toastr.error ( 'Inventory loading error occurred', 'Service Error', {
						timeOut : 3000,
					} );
				} )
			);
		}
	}
	
	public onHover(e, index = null) {
		const aux = e.target.offsetWidth < e.target.scrollWidth;
		this.hideTooltip = !aux;
	}
	
	public getContentDialogHeight () : number {
		return (
			window.innerHeight - (
				this.actionbar.nativeElement.offsetHeight +
				this.actionbarfoot.nativeElement.offsetHeight +
				48
			) - 77
		);
	}
	
	public getScrollHeight () : number {
		return this.getContentDialogHeight () - (
			this.tableHeader.nativeElement.offsetHeight
			+ this.paginator.nativeElement.offsetHeight + 52 + (
				this.viewFilters ? 52 : 0
			)
		);
	}

	public onPage($event) {
		this.setPage({
	         offset: $event.first / $event.rows,
	         size: $event.rows
	     });
	}
	
	public onColumnResize ( event ) {
		this.tableExtensionService.onColumnResize ( this.cols, event );
	}
	
	public onSort(col) {
		if (col.sort) {
		  if (this.page.filterParams.hasOwnProperty('SORT_COLUMN')) {
			if (this.page.filterParams['SORT_COLUMN'] === col.field) {
			  if (this.page.filterParams['SORT_DIRECTION'] === 'ASC') {
				this.page.filterParams['SORT_DIRECTION'] = 'DESC';
			  } else {
				delete this.page.filterParams['SORT_DIRECTION'];
				delete this.page.filterParams['SORT_COLUMN'];
			  }
			} else {
			  this.page.filterParams['SORT_COLUMN'] = col.field;
			  this.page.filterParams['SORT_DIRECTION'] = 'ASC';
			}
		  } else {
			this.page.filterParams['SORT_COLUMN'] = col.field;
			this.page.filterParams['SORT_DIRECTION'] = 'ASC';
		  }
	
		  this.parseSort(this.page.filterParams);
		  this.getData();
		}
	  }
	
	  private parseSort(filterParams) {
		const sortDetails = this.sortService.parseSort(filterParams);
		this.sortField = sortDetails.sortField;
		this.sortOrder = sortDetails.sortOrder;
	  }
	  
	  public parseParams(filterParams) {
		if (filterParams !== undefined && filterParams !== null) {
		  let showFilters = false;
		  let sortApplied = false;
		  for (const key of Object.keys(filterParams)) {
			const value = filterParams[key];
			if (value && value.toString() !== '') {
			  if (key.toString() === 'page') {
				this.page.pageNumber = Number(value);
			  } else if (key.toString() === 'perPage') {
				this.page.size = Number(value);
				this.size = Number(value);
			  } else if (
				  key.toString() === 'SORT_COLUMN' ||
				  key.toString() === 'SORT_DIRECTION'
			  ) {
				this.page.filterParams[key] = value;
				sortApplied = true;
			  } else {
				this.page.filterParams[key] = value;
				showFilters = true;
			  }
			}
		  }
		  this.viewFilters = showFilters;
		  this.filtersApplied = showFilters;
		  this.sortApplied = sortApplied;
	
		  this.parseSort(filterParams);
		}
	  }

	public newClick () {
		this.loading = true;
		this.cancelEditClick();
		this.newline = Number(this.page.size * this.page.pageNumber);
		this.loading = false;
	}
	
	public editClick (selectedIndex) {
		this.loading = true;
		this.cancelClick();
		let pageStart = Number(this.page.size * this.page.pageNumber);
		let selectedRow = selectedIndex - pageStart;
		this.rowSelection = selectedIndex;
		this.loadPartInfo(selectedRow);
		this.editRow = true;
		this.loadCustomerData();
		this.selected = [];
		this.loading = false;
	}
	
	public deleteClick (selectedIndex) {
		this.loadPartInfo(selectedIndex);

		this.confirmationService.confirm ( {
			header : 'Delete',
			message : 'Are you sure you want to delete "' + (this.edit.ZO1_ITEM) + '" ?',
			accept : () => {
				this.userActionLoading = true;
				this.zeroOutInventoryService.delete ( this.edit.ZO1_ID ).subscribe (
					( result ) => {
						if ( result.success ) {
							this.toastr.info ( 'Delete request successful', 'Request Complete' );
							this.userActionLoading = false;
							this.getData (false);
						} else {
							this.toastr.error ( 'Unable to complete delete request', 'Error' );
							this.userActionLoading = false;
						}
					},
					() => {
						this.toastr.error ( 'Request timed out', 'Service Error' );
						this.userActionLoading = false;
					}
				);
			}
		} );
	}
	
	public refreshClick () {
		this.loading = true;
		this.errors = [];
		this.getData (false);
		this.editRow = false;
		this.newline = null;
		this.loading = false;
	}
	
	public onWindowResize () {
		setTimeout ( () => {
			this.scrollHeight = this.getScrollHeight() + 'px';
			window.dispatchEvent ( new Event ( 'resize' ) );
		}, 150 );
	}
	
	public screenHeightListener() {
		if (!this.viewportHeightListener) {
		  return;
		}
		if (window.innerHeight !== this.viewportHeight) {
		  this.viewportHeight = window.innerHeight;
		  this.onWindowResize();
		}
		setTimeout(() => {
		  this.screenHeightListener();
		}, 500);
	  }

	public saveClick () {
		if(this.edit.ZO1_RESTRICT_INVENTORY_FIXED) {
			this.edit.ZO1_DISPLAY_FIXED_QUANTITY = null;
			this.edit.ZO1_DISPLAY_INVENTORY_FIXED = null;
		} else {
			this.edit.ZO1_START_DATE = null;
			this.edit.ZO1_END_DATE = null;
		}
		
		if(this.edit.ZO1_DISPLAY_INVENTORY_FIXED) {
			this.edit.ZO1_DISPLAY_FIXED_QUANTITY = null;
		} else if (this.edit.ZO1_DISPLAY_FIXED_QUANTITY) {
			this.edit.ZO1_DISPLAY_INVENTORY_FIXED = null;
		}
		this.zeroOutInventoryService.update(this.edit).subscribe(result => {
			if (result.success) {
				this.loading = true;
				this.refreshClick ();
				this.toastr.success('Zero Out Inventory updated!', 'Success');
				this.loading = false;
			} else {
				this.errors = result.errors;
			}
		}, () => {
			this.toastr.error('Request timed out', 'Service Error');
		});
	}
	
	public cancelClick () {
		this.loading = true;
		if(this.record.ZO1_RESTRICT_INVENTORY_FIXED) {
			this.record.ZO1_DISPLAY_FIXED_QUANTITY = 0;
			this.record.ZO1_DISPLAY_INVENTORY_FIXED = 0;
		}
		if(this.record.ZO1_DISPLAY_INVENTORY_FIXED) {
			this.record.ZO1_DISPLAY_FIXED_QUANTITY = 0;
		} else if (this.record.ZO1_DISPLAY_FIXED_QUANTITY) {
			this.record.ZO1_DISPLAY_INVENTORY_FIXED = 0;
		}
		this.clearRecord();
		this.newline = null;
		this.refreshClick();
	}
	
	public backClick () {
		this.loading = false;
		this.router.navigate(['/dashboard']);
	}
	
	public cancelEditClick () {
		if(!this.edit.ZO1_RESTRICT_INVENTORY_FIXED) {
			this.edit.ZO1_DISPLAY_FIXED_QUANTITY = null;
			this.edit.ZO1_DISPLAY_INVENTORY_FIXED = null;
			this.edit.ZO1_START_DATE = null;
			this.edit.ZO1_END_DATE = null;
		}
		
		if(this.edit.ZO1_DISPLAY_INVENTORY_FIXED) {
			this.edit.ZO1_DISPLAY_FIXED_QUANTITY = null;
		} else if (this.edit.ZO1_DISPLAY_FIXED_QUANTITY) {
			this.edit.ZO1_DISPLAY_INVENTORY_FIXED = null;
		}
		this.editRow = false;
		this.rowSelection = null;
		this.selected = [];
		this.refreshClick();
	}
	
	public addClick() {
		if(this.record.ZO1_RESTRICT_INVENTORY_FIXED) {
			this.record.ZO1_DISPLAY_FIXED_QUANTITY = null;
			this.record.ZO1_DISPLAY_INVENTORY_FIXED = null;
		} else {
			this.record.ZO1_START_DATE = null;
			this.record.ZO1_END_DATE = null;
		}
		
		if(this.record.ZO1_DISPLAY_INVENTORY_FIXED) {
			this.record.ZO1_DISPLAY_FIXED_QUANTITY = null;
		} else if (this.record.ZO1_DISPLAY_FIXED_QUANTITY) {
			this.record.ZO1_DISPLAY_INVENTORY_FIXED = null;
		}
		this.zeroOutInventoryService.create(this.record).subscribe(result => {
			if (result.success) {
				this.refreshClick();
				this.clearRecord();
				this.newline = null;
				this.toastr.success('Zero Out Inventory item created!', 'Success');
				this.loading = false;
			} else {
				this.errors = result.errors;
			}
		}, () => {
			this.toastr.error('Request timed out', 'Service Error');
		});
	}
	
	private loadPartInfo(selectedRow) {
		this.edit = this.rows[ selectedRow ];
		this.edit.ZO1_ID = Number(this.rows[selectedRow]['ZO1_ID']);
		this.edit.CO1_ID = Number(this.rows[ selectedRow ]['CO1_ID']);
		this.edit.ZO1_BRANCH = this.rows[ selectedRow ]['ZO1_BRANCH'];
		this.edit.CU1_ID = Number(this.rows[ selectedRow ]['CU1_ID']);
		this.edit.ZO1_ITEM = this.rows[ selectedRow ]['ZO1_ITEM'];
		this.edit.ZO1_RESTRICT_INVENTORY_FIXED = Number(this.rows[ selectedRow ]['ZO1_RESTRICT_INVENTORY_FIXED']);
		this.edit.ZO1_DISPLAY_INVENTORY_FIXED = Number(this.rows[ selectedRow ]['ZO1_DISPLAY_INVENTORY_FIXED']);
		this.edit.ZO1_DISPLAY_FIXED_QUANTITY = Number(this.rows[ selectedRow ]['ZO1_DISPLAY_FIXED_QUANTITY']);
		this.edit.ZO1_START_DATE = this.rows[ selectedRow ]['ZO1_START_DATE'];
		this.edit.ZO1_END_DATE = this.rows[ selectedRow ]['ZO1_END_DATE'];
	}
	
	public toggleFilterView() {
		this.viewFilters = !this.viewFilters;
		this.onWindowResize();
	}
	
	public buttonPress(e, col, value) {
		if (e.keyCode === 13) {
			this.filterChanged(col, value);
		}
	}
	
	public clearFilters() {
		this.page.filterParams = {};
		this.filtersApplied = false;
		this.sortApplied = false;
		this.sortField = '';
		this.sortOrder = 0;
		this.page.pageNumber = 0;
		this.setPage({ offset: this.page.pageNumber }, false);
	}
	
	public filterChanged(col, value) {
		this.loading = true;

		if ((col=='ZO1_START_DATE') || (col=='ZO1_END_DATE')) {
			value=this.datesService.format(value);
		}

		if(col && value) {
			this.page.filterParams[col] = value;
		}
		console.log('col: ' + col);
		console.log('value: ' + value);
		this.filtersApplied = Object.keys(this.page.filterParams).length > 0;
		console.log('filters applied: ' + this.filtersApplied);
		this.page.pageNumber = 0;
		this.setPage({ offset: this.page.pageNumber });
	}
	
	private clearRecord () {
		this.record.ZO1_ID = null;
		this.record.CO1_ID = null;
		this.record.ZO1_BRANCH = '';
		this.record.CU1_ID = null;
		this.record.ZO1_ITEM = '';
		this.record.ZO1_RESTRICT_INVENTORY_FIXED = null;
		this.record.ZO1_DISPLAY_INVENTORY_FIXED = null;
		this.record.ZO1_DISPLAY_FIXED_QUANTITY = null;
		this.record.ZO1_START_DATE = '';
		this.record.ZO1_END_DATE = '';
	}

	loadCustomerData() {

		let tmp_CO1_ID = this.editRow?this.edit.CO1_ID:this.record.CO1_ID;

		if (tmp_CO1_ID != null) {
			this.customerService.getShortCustomerList(tmp_CO1_ID).subscribe(response => {
				this.customerList = response.customerData;

				let tmp_CU1_ID = this.editRow?this.edit.CU1_ID:this.record.CU1_ID;
				
				if ((tmp_CU1_ID != null) && (this.customerList.find(customer => customer.CU1_ID==tmp_CU1_ID) === undefined)) {
					this.record.CU1_ID = null;
					this.edit.CU1_ID = null;
				}
			});	
		} else {
			this.customerList = [];
		}
	}

	onCompanyChange($event) {
		this.loadCustomerData();
	}
}



// WEBPACK FOOTER //
// /home/ubuntu/edi/angular/app/views/zero-out-inventory/zero-out-inventory.component.ts